import { createContext, ReactNode, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { AccessTokenPayload, AuthContextType, Login } from "../../types/auth";
import { AxiosError } from "axios";
import { useToast } from "./ToastProvider";

export const AuthContext = createContext<AuthContextType | null>(null);

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { showToast } = useToast();
  const [parsedToken, setParsedToken] = useState<AccessTokenPayload | null>(
    null,
  );

  useEffect(() => {
    const token = Cookies.get("accessToken");
    if (token) {
      try {
        setParsedToken(jwtDecode<AccessTokenPayload>(token));
      } catch {
        Cookies.remove("accessToken");
        setParsedToken(null);
      }
    }
  }, []);

  const login = (token: Login) => {
    Cookies.set("accessToken", token.access_token, {
      expires: new Date(Date.now() + token.expires_in * 1000),
    });
    Cookies.set("refreshToken", token.refresh_token, {
      expires: new Date(Date.now() + 3600 * 24 * 6 * 1000),
    });

    const decodedToken =
      jwtDecode<AccessTokenPayload>(token.access_token) || {};
    setParsedToken({ ...decodedToken, refreshToken: token.refresh_token });
  };

  const logout = () => {
    Cookies.remove("accessToken");
    Cookies.remove("refreshToken");
    setParsedToken(null);
  };

  const handleError = (error: AxiosError) => {
    const status = error.response?.status;

    if (status === 401) {
      showToast("error", `Unauthorized. Please login again`);
      logout();
    } else if (status === 404) {
      showToast("error", "The requested resource was not found");
    } else if (status === 500) {
      showToast("error", "Internal server error");
    } else {
      showToast("error", `Request failed with status ${status}`);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        parsedToken,
        login,
        logout,
        handleError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
